import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Idle + absolute session timeout with automatic logout.
 *
 * - IDLE timeout: logs the user out after a period of no interaction
 *   (mousemove / keydown / click / scroll / touch).
 * - ABSOLUTE timeout: caps the total session length regardless of activity.
 *
 * Added for the OWASP / VAPT hardening pass (Session Management requirement).
 * Timers run outside Angular's zone so they don't trigger change-detection on
 * every mouse move; only the actual logout re-enters the zone.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
    /** Inactivity allowed before auto-logout (ms). 20 minutes. */
    private readonly IDLE_TIMEOUT_MS = 20 * 60 * 1000;
    /** Hard cap on total session length (ms). 8 hours. */
    private readonly ABSOLUTE_TIMEOUT_MS = 8 * 60 * 60 * 1000;
    /** Throttle activity handling so we reset the timer at most once / sec. */
    private readonly ACTIVITY_THROTTLE_MS = 1000;

    private idleTimer: any = null;
    private absoluteTimer: any = null;
    private lastActivity = 0;
    private running = false;
    private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    private readonly boundActivity = () => this.onActivity();

    constructor(private router: Router, private zone: NgZone) { }

    /** Begin monitoring. Safe to call repeatedly (re-arms timers). */
    start(): void {
        if (typeof window === 'undefined') { return; }
        this.stop();
        this.running = true;
        this.zone.runOutsideAngular(() => {
            this.activityEvents.forEach(evt =>
                window.addEventListener(evt, this.boundActivity, { passive: true }));
            this.armIdleTimer();
            this.absoluteTimer = setTimeout(() => this.expire('absolute'), this.ABSOLUTE_TIMEOUT_MS);
        });
    }

    /** Stop monitoring and clear timers/listeners (call on logout). */
    stop(): void {
        this.running = false;
        if (typeof window !== 'undefined') {
            this.activityEvents.forEach(evt => window.removeEventListener(evt, this.boundActivity));
        }
        if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
        if (this.absoluteTimer) { clearTimeout(this.absoluteTimer); this.absoluteTimer = null; }
    }

    private onActivity(): void {
        const now = Date.now();
        if (now - this.lastActivity < this.ACTIVITY_THROTTLE_MS) { return; }
        this.lastActivity = now;
        this.armIdleTimer();
    }

    private armIdleTimer(): void {
        if (this.idleTimer) { clearTimeout(this.idleTimer); }
        this.idleTimer = setTimeout(() => this.expire('idle'), this.IDLE_TIMEOUT_MS);
    }

    private expire(reason: 'idle' | 'absolute'): void {
        if (!this.running) { return; }
        this.stop();
        // Re-enter Angular zone for navigation / storage clearing.
        this.zone.run(() => {
            try { localStorage.clear(); } catch { /* ignore */ }
            try { sessionStorage.clear(); } catch { /* ignore */ }
            this.router.navigate(['/internal'], {
                replaceUrl: true,
                queryParams: { sessionExpired: reason },
            });
        });
    }
}
